import React, { FC, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, Divider, Grid } from '@mui/material';
import MediaUploader from './MediaUploader';
import UploadPreviewCard from './UploadPreviewCard';
import MediaGallery from './MediaGallery';
import { ProjectMedia } from 'src/api/Api';
import { useAppState } from 'src/state-management/state-provider';

const ProjectMediaPage: FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { dispatch } = useAppState();
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);

  const handleFilesAccepted = (files: File[]) => {
    const newFiles = files.filter(file =>
      !filesToUpload.some(existingFile => existingFile.name === file.name) &&
      !uploadedFiles.includes(file.name)
    );
    setFilesToUpload((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleRemoveFile = useCallback((fileToRemove: File) => {
    setFilesToUpload((prevFiles) => prevFiles.filter(file => file !== fileToRemove));
  }, []);

  const handleUploadComplete = useCallback((fileName: string) => {
    setUploadedFiles(prev => [...prev, fileName]);
  }, []);

  const handleFinalize = useCallback((mediaItem: ProjectMedia) => {
    dispatch({
      type: 'ADMIN_ADD_PROJECT_MEDIA',
      payload: { projectId: mediaItem.project_id, mediaItem }
    });
    // Remove the file from the upload list
    setFilesToUpload(prevFiles => prevFiles.filter(f => f.name !== mediaItem.file_name));
  }, [dispatch]);

  if (!projectId) {
    return <Typography sx={{ color: '#FFF' }}>Project ID is missing.</Typography>;
  }

  return (
    <Box sx={{ p: 4, backgroundColor: '#0A0A0A', minHeight: '100vh', color: '#FFF' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: '#FFF' }}>
        Manage Project Media
      </Typography>

      <Box sx={{ mb: 6 }}>
        <MediaUploader onFilesAccepted={handleFilesAccepted} />
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '12px' }}>
          Uploading Files
        </Typography>
        {filesToUpload.length > 0 ? (
          <Grid container spacing={3}>
            {filesToUpload.map((file, index) => (
              <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                <UploadPreviewCard
                  file={file}
                  projectId={projectId}
                  onRemove={handleRemoveFile}
                  onUploadComplete={handleUploadComplete}
                  onFinalize={handleFinalize}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ p: 4, border: '1px dashed #222', borderRadius: '16px', textAlign: 'center' }}>
            <Typography sx={{ color: '#666' }}>No files selected for upload.</Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 6, borderColor: '#222' }} />

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '12px' }}>
          Existing Gallery
        </Typography>
        <MediaGallery projectId={projectId} />
      </Box>
    </Box>
  );
};

export default ProjectMediaPage;
